import { useState, useCallback, useRef, useEffect } from 'react';
import { message } from 'antd';
import { pw9220Api, PunchResponse } from '../../../services/pw9220Api';

export type LogCode = 'Q' | 'S' | 'T' | 'V';

interface ModeInfo {
  code: LogCode;
  label: string;
  greeting: string;
}

const MODES: Record<LogCode, ModeInfo> = {
  Q: { code: 'Q', label: '> 上班簽到 <', greeting: '祝您工作愉快!!' },
  S: { code: 'S', label: '< 外出簽退 >', greeting: '祝您工作愉快!!' },
  T: { code: 'T', label: '> 進入簽到 <', greeting: '祝您工作愉快!!' },
  V: { code: 'V', label: '< 下班簽退 >', greeting: '謝謝您的努力!!' },
};

export function usePunchClock() {
  const [logCode, setLogCode] = useState<LogCode>('Q');
  const [employeeName, setEmployeeName] = useState('***');
  const [statusMessage, setStatusMessage] = useState('祝您工作愉快!!');
  const [lastResponse, setLastResponse] = useState<PunchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [flashBig, setFlashBig] = useState(false);
  const tickCount = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Switch mode manually (BR-B01~B04)
  const switchMode = useCallback((code: LogCode) => {
    setLogCode(code);
    setEmployeeName('***');
    tickCount.current = 0;
    if (code === 'V') {
      setStatusMessage('謝謝您的努力!!');
    }
  }, []);

  // Auto-switch mode based on time (BR-T02)
  const autoSwitchMode = useCallback(() => {
    const now = new Date();
    const timeStr = now.toTimeString().substring(0, 8); // HH:MM:SS
    if (timeStr <= '09:30:59') {
      setLogCode('Q');
    } else if (timeStr < '16:30:00') {
      setLogCode('S');
    } else {
      setLogCode('V');
    }
    setEmployeeName('***');
    setStatusMessage('祝您工作愉快!!');
  }, []);

  // Timer tick (BR-T01, BR-T02)
  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setFlashBig(prev => !prev);
      tickCount.current += 1;
      if (tickCount.current > 15) {
        tickCount.current = 0;
        autoSwitchMode();
      }
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [autoSwitchMode]);

  // Process punch (BR-V01~BR-C05)
  const punch = useCallback(async (cardValue: string) => {
    if (!cardValue.trim()) return;
    setLoading(true);
    tickCount.current = 0;
    try {
      const resp = await pw9220Api.punch({
        card_value: cardValue,
        log_code: logCode,
      });
      setLastResponse(resp);
      if (resp.employee_name) {
        setEmployeeName(resp.employee_name);
      }
      setStatusMessage(resp.message);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { errors?: { message: string }[]; error?: string } } };
      const errors = error.response?.data?.errors;
      if (errors && Array.isArray(errors)) {
        message.error(errors.map(e => e.message).join('; '));
        setEmployeeName('*錯誤*請重刷或通知人事部門!');
      } else {
        message.error(error.response?.data?.error || '打卡失敗');
        setEmployeeName('*錯誤*請重刷或通知人事部門!');
      }
    } finally {
      setLoading(false);
    }
  }, [logCode]);

  const modeInfo = MODES[logCode];

  return {
    logCode,
    modeLabel: modeInfo.label,
    employeeName,
    statusMessage,
    lastResponse,
    loading,
    flashBig,
    switchMode,
    punch,
  };
}
